import useConversationStorage from './useConversationStorage';
import { useMemo } from 'react';

/**
 * Represents the state of a live chat.
 */
export enum LiveChatState {
    notStarted = 'notStarted',
    active = 'active',
    inactive = 'inactive',
    started = 'started',
    stopped = 'stopped',
}

/**
 * Interface representing the stored state of a Live Chat.
 */
interface LiveChatStoredState {
    currentValue: LiveChatState;
    lastUpdatedAt?: number;
    createdAt: number;
}

/**
 * Represents a record of live chat state.
 */
export type LiveChatStateRecord = Record<string, LiveChatStoredState>;

/**
 * Returns the default stored state for the Live Chat.
 */
const getDefaultStoredState = (): LiveChatStoredState => {
    return {
        currentValue: LiveChatState.notStarted,
        createdAt: Date.now(),
    };
};

/**
 * Custom hook to manage the state of a live chat using conversation storage.
 *
 * @param {string} token - The token identifying the live chat session.
 *
 * @returns {Array} - An array containing the current state of the live chat and a function to update the state.
 */
export default function useLiveChatState(
    token: string
): [LiveChatState, (value: LiveChatState) => void] {
    const [liveChatState, setLiveChatState] =
        useConversationStorage<LiveChatStateRecord>('liveChatState', {
            [token]: getDefaultStoredState(),
        });

    return useMemo(() => {
        const tokenState = liveChatState[token] ?? getDefaultStoredState();
        return [
            tokenState.currentValue,
            (value) => {
                tokenState.currentValue = value;
                liveChatState[token] = tokenState;
                setLiveChatState(liveChatState);
            },
        ];
    }, [liveChatState]);
}
